<template>
  <div
    :style="{ 'z-index': getPopupZindex }"
    :class="{ 'survey-results-popup-overlay': true, 'is-visible': isVisible }"
    @click.stop.prevent="() => false">
    <div :class="{ 'survey-results-popup': true, 'is-visible': isVisible, 'is-loading': isLoading }">
      <a
        href="#"
        class="survey-results-popup-close"
        @click.prevent="close()">
        &times;
      </a>

      <h2>
        {{ $t('surveyResultsPopup.title') }}

        <span
          v-if="surveyData.scoring"
          class="survey-results-popup-scoring">
          {{ $t('surveyResultsPopup.score') }}: <strong>{{ surveyData.scoring }}</strong>
        </span>
      </h2>

      <div
        v-if="isLoading"
        class="survey-results-popup-empty-state">
        {{ $t('common.ui.loadingData') }}
      </div>

      <div class="survey-results-popup-content">
        <dl>
          <template v-for="(question, index) of surveyData.questions">
            <dt :key="'question-title-' + index">
              {{ question.question }}
            </dt>
            <dd :key="'question-answer-' + index">
              {{ question.answer }}
            </dd>
          </template>
        </dl>
      </div>

      <btn
        type="popup-full"
        @click.native="close">
        {{ $t('common.ui.close') }}
      </btn>
    </div>
  </div>
</template>

<script>
import Configuration from './../mixins/Configuration';
import ErrorUtils from './../helpers/ErrorUtils.js';
import PopupZindex from './../mixins/PopupZindex';
import UserTypes from './../mixins/UserTypes';
import WordVariations from './../mixins/WordVariations';

export default {
  name: 'survey-results-popup',
  mixins: [
    Configuration,
    PopupZindex,
    UserTypes,
    WordVariations
  ],
  data () {
    return {
      surveyID: 0,
      isLoading: true,
      isVisible: false,
      surveyData: false
    };
  },
  mounted () {
    this.$bus.$on('show-survey-results-popup', this.show);
    this.$bus.$on('hide-survey-results-popup', this.close);
  },
  methods: {
    show (id = null, questions = [], scoring = 0) {
      if (id) {
        this.surveyID = id;
        this.isLoading = true;
        this.loadData();
      } else {
        this.surveyID = 0;
        this.surveyData = {
          questions,
          scoring
        };
        this.isLoading = false;
      }

      this.isVisible = true;
      this.increasePopupZindex();
    },
    loadData () {
      this.$http.get('/panel/inquiries-surveys/result/' + this.surveyID).then(response => {
        this.surveyData = response.data;
        this.isLoading = false;
      }).catch(error => {
        ErrorUtils.handleSubmitError(error, '/panel/inquiries-surveys/result/' + this.surveyID, this.$router, this.$bus, this.loadData.bind(this));
        this.isLoading = false;
      });
    },
    close () {
      this.surveyID = 0;
      this.isVisible = false;
      this.isLoading = true;
      this.surveyData = false;
      this.removePopupZindex();
    }
  },
  beforeDestroy () {
    this.$bus.$off('show-survey-results-popup', this.show);
    this.$bus.$off('hide-survey-results-popup', this.close);
  }
};
</script>

<style lang="scss" scoped>
@import '../../assets/scss/variables.scss';

.survey-results-popup {
  background: $color-light;
  border-radius: $border-radius-small;
  box-shadow: 0 1rem 2rem rgba(0, 0, 0, .125);
  display: none;
  left: 50%;
  max-height: 90vh;
  padding: 3rem 4rem 6rem 4rem;
  position: fixed;
  top: 50%;
  transform: translateX(-50%) translateY(-50%);
  width: 720px;
  z-index: 10000;

  &-scoring {
    color: $color-text-light;
    display: block;
    font-size: 16px;
    margin-left: auto;

    & > strong {
      color: $color-primary;
    }
  }

  &-overlay {
    background: rgba(0, 0, 0, .25);
    height: 100%;
    left: 0;
    opacity: 0;
    pointer-events: none;
    position: fixed;
    top: 0;
    transition: opacity .25s ease-out;
    width: 100vw;
    will-change: opacity;
    z-index: 10000;

    &.is-visible {
      opacity: 1;
      pointer-events: auto;
    }
  }

  &.is-visible {
    display: block;
  }

  &.is-loading {
    h2,
    p,
    dl {
      opacity: 0;
    }
  }

  .survey-results-popup-empty-state {
    align-items: center;
    color: $color-text-light;
    display: flex;
    font-size: 2.4rem;
    height: 100%;
    justify-content: center;
    left: 0;
    padding: 3.2rem 0;
    position: absolute;
    text-align: center;
    top: 0;
    width: 100%;
    z-index: 10;
  }

  & > h2 {
    align-items: flex-end;
    display: flex;
    margin: 0;

    & > small {
      color: $color-text-light;
      margin-left: 1rem;
    }

    .button {
      position: relative;
      top: 2px;

      &:first-of-type {
        margin-left: auto;
      }

      &:last-of-type {
        margin-right: 0;
      }
    }
  }

  &-content {
    max-height: 77vh;
    overflow: auto;
    -webkit-overflow-scrolling: touch;

    & > dl {
      display: flex;
      flex-wrap: wrap;

      & > dt {
        background: $color-bg-medium-alt;
        border-radius: 5px;
        font-weight: bold;
        line-height: 1.4;
        margin: 0;
        padding: 1rem;
        width: 100%;
      }

      & > dd {
        line-height: 1.4;
        margin: 0;
        padding: 1.5rem 1rem;
        width: 100%;
      }
    }
  }

  .button-popup-full {
    border-radius: 0 0 $border-radius-small $border-radius-small;
    color: $color-text;
  }

  &-close {
    background: $color-danger;
    border-radius: 50%;
    color: $color-light;
    font-size: 3.6rem;
    font-weight: bold;
    height: 3.6rem;
    line-height: 3.5rem;
    position: absolute;
    right: -1.8rem;
    text-align: center;
    top: -1.8rem;
    width: 3.6rem;
  }
}

@media (max-height: 760px) and (min-width: 800px) {
  .survey-results-popup-content {
    max-height: 60vh;
    margin-top: 3rem;
  }
}

@media (max-width: 800px) {
  .survey-results-popup {
    border-radius: 0;
    height: 100%;
    left: 0;
    max-height: 100%;
    padding: 2rem 2rem 8rem 2rem;
    top: 0;
    transform: none;
    width: 100vw;

    .survey-results-popup-close {
      display: none;
    }

    .survey-results-popup-content {
      height: 100%;
      margin-top: 2rem;
      max-height: 97.5%;
    }
  }
}

@media (max-width: 640px) {
  .survey-results-popup {
    & > h2 {
      font-size: 20px;
      position: relative;
      top: -10px;

      & > small {
        position: relative;
        top: -2px;
      }

      & > .button {
        left: 0;
        position: absolute;
        top: 30px;
        width: calc(50% - .5rem);

        & + .button {
          left: auto;
          right: 0;
        }
      }
    }

    .survey-results-popup-content {
      height: 100%;
      margin-top: 0rem;
    }
  }
}

@media (max-width: 480px) {
  .survey-results-popup {
    &-content {
      & > dl > dt,
      & > dl > dd {
        width: 100%;
      }
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/ui/SurveyResultsPopup.vue